import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { auth, RecaptchaVerifier, signInWithPhoneNumber } from "@/lib/firebase";
import { toast } from "sonner";
import { Phone, Shield, Loader2, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";

// Global variables outside the component
let recaptchaWidgetId = null;
let recaptchaVerifierInstance = null;

export default function PhoneVerification({ isOpen, onClose, onVerified, currentMobile }) {
  const [step, setStep] = useState("phone"); // phone, otp, success
  const [phoneNumber, setPhoneNumber] = useState(currentMobile || "");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [isLoading, setIsLoading] = useState(false);
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [countdown, setCountdown] = useState(0);
  const otpRefs = useRef([]);

  // Countdown timer for resend
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const formatPhoneNumber = (phone) => {
    let cleaned = phone.replace(/\D/g, "");
    if (cleaned.startsWith("0")) {
      cleaned = cleaned.substring(1);
    }
    if (!cleaned.startsWith("91") && cleaned.length === 10) {
      cleaned = "91" + cleaned;
    }
    return "+" + cleaned;
  };

  const cleanupRecaptcha = () => {
    // Cleanup existing recaptcha instance
    if (recaptchaVerifierInstance) {
      try {
        recaptchaVerifierInstance.clear();
      } catch (e) {
        console.log("Recaptcha clear error:", e);
      }
      recaptchaVerifierInstance = null;
    }
    recaptchaWidgetId = null;
    
    // Clear the container safely (avoid innerHTML for XSS prevention)
    const container = document.getElementById('recaptcha-container');
    if (container) {
      while (container.firstChild) {
        container.removeChild(container.firstChild);
      }
    }
  };

  const handleSendOTP = async () => {
    const cleanedPhone = phoneNumber.replace(/\D/g, "");
    if (cleanedPhone.length < 10) {
      toast.error("Please enter a valid 10-digit mobile number");
      return;
    }

    setIsLoading(true);
    
    try {
      // Cleanup before creating fresh RecaptchaVerifier
      cleanupRecaptcha();

      // Create fresh RecaptchaVerifier
      recaptchaVerifierInstance = new RecaptchaVerifier(auth, 'recaptcha-container', {
        size: 'invisible',
        callback: () => {
          console.log('reCAPTCHA verified');
        },
        'expired-callback': () => {
          console.log('reCAPTCHA expired');
          toast.error('reCAPTCHA expired. Please try again.');
          cleanupRecaptcha();
        }
      });

      // Render the recaptcha
      recaptchaWidgetId = await recaptchaVerifierInstance.render();
      console.log('reCAPTCHA rendered with widget ID:', recaptchaWidgetId);

      const formattedPhone = formatPhoneNumber(phoneNumber);
      console.log("Sending OTP to:", formattedPhone);

      // Send OTP
      const result = await signInWithPhoneNumber(auth, formattedPhone, recaptchaVerifierInstance);
      
      setConfirmationResult(result);
      setStep("otp");
      setCountdown(60);
      toast.success("OTP sent to your mobile number");
      
    } catch (error) {
      console.error("Error sending OTP:", error);
      
      // Cleanup on error
      cleanupRecaptcha();

      if (error.code === "auth/invalid-phone-number") {
        toast.error("Invalid phone number format. Please check and try again.");
      } else if (error.code === "auth/too-many-requests") {
        toast.error("Too many attempts. Please try again after some time.");
      } else if (error.code === "auth/captcha-check-failed") {
        toast.error("Security verification failed. Please try again.");
      } else if (error.code === "auth/quota-exceeded") {
        toast.error("SMS quota exceeded. Please try again later.");
      } else {
        toast.error(error.message || "Failed to send OTP. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpChange = (index, value) => {
    if (value.length > 1) {
      value = value.slice(-1);
    }
    if (!/^\d*$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (pastedData.length === 6) {
      setOtp(pastedData.split(""));
      otpRefs.current[5]?.focus();
    }
  };

  const handleVerifyOTP = async () => {
    const otpCode = otp.join("");
    if (otpCode.length !== 6) {
      toast.error("Please enter the complete 6-digit OTP");
      return;
    }

    setIsLoading(true);
    try {
      const userCredential = await confirmationResult.confirm(otpCode);
      const idToken = await userCredential.user.getIdToken();
      
      const formattedPhone = formatPhoneNumber(phoneNumber);
      await api.post("/auth/verify-phone", {
        firebase_token: idToken,
        phone_number: formattedPhone
      });

      setStep("success");
      toast.success("Mobile number verified successfully!");
      
      // Cleanup recaptcha after successful verification
      cleanupRecaptcha();
      
      setTimeout(() => {
        onVerified && onVerified(formattedPhone);
        onClose();
        resetState();
      }, 2000);
    } catch (error) {
      console.error("Error verifying OTP:", error);
      if (error.code === "auth/invalid-verification-code") {
        toast.error("Invalid OTP. Please check and try again.");
      } else if (error.code === "auth/code-expired") {
        toast.error("OTP expired. Please request a new one.");
      } else {
        toast.error(error.response?.data?.detail || "Verification failed. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOTP = () => {
    if (countdown > 0) return;
    
    // Go back to phone step to resend
    setStep("phone");
    setOtp(["", "", "", "", "", ""]);
    setConfirmationResult(null);
  };

  const resetState = () => {
    setStep("phone");
    setPhoneNumber(currentMobile || "");
    setOtp(["", "", "", "", "", ""]);
    setConfirmationResult(null);
    setCountdown(0);
  };

  const handleClose = () => {
    cleanupRecaptcha();
    resetState();
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-orange-500" />
            {step === "success" ? "Verification Complete" : "Verify Mobile Number"}
          </DialogTitle>
          <DialogDescription>
            {step === "phone" && "Enter your mobile number to receive a verification code"}
            {step === "otp" && "Enter the 6-digit OTP sent to your mobile"}
            {step === "success" && "Your mobile number has been verified"}
          </DialogDescription>
        </DialogHeader>

        <div id="recaptcha-container"></div>

        <div className="space-y-4 py-4">
          {step === "phone" && (
            <>
              <div className="space-y-2">
                <Label htmlFor="phone">Mobile Number</Label>
                <div className="flex gap-2">
                  <div className="flex items-center px-3 bg-slate-100 rounded-md border border-slate-200">
                    <span className="text-sm text-slate-600">+91</span>
                  </div>
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="9876543210"
                    value={phoneNumber.replace(/^\+?91/, "")}
                    onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, "").slice(0, 10))}
                    className="flex-1"
                    maxLength={10}
                    data-testid="phone-input"
                  />
                </div>
                <p className="text-xs text-slate-500">
                  We'll send a verification code to this number
                </p>
              </div>
              
              <Button
                onClick={handleSendOTP}
                disabled={isLoading || phoneNumber.replace(/\D/g, "").length < 10}
                className="w-full bg-orange-500 hover:bg-orange-600"
                data-testid="send-otp-btn"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending OTP...
                  </>
                ) : (
                  <>
                    <Phone className="w-4 h-4 mr-2" />
                    Send OTP
                  </>
                )}
              </Button>
            </>
          )}

          {step === "otp" && (
            <>
              <div className="space-y-2">
                <Label>Enter OTP</Label>
                <div className="flex justify-center gap-2">
                  {otp.map((digit, index) => (
                    <Input
                      key={index}
                      ref={(el) => (otpRefs.current[index] = el)}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleOtpChange(index, e.target.value)}
                      onKeyDown={(e) => handleOtpKeyDown(index, e)}
                      onPaste={index === 0 ? handleOtpPaste : undefined}
                      className="w-12 h-12 text-center text-xl font-semibold"
                      data-testid={`otp-input-${index}`}
                    />
                  ))}
                </div>
                <p className="text-xs text-slate-500 text-center">
                  OTP sent to +91 {phoneNumber.replace(/^\+?91/, "").replace(/(\d{5})(\d{5})/, "$1 $2")}
                </p>
              </div>

              <Button
                onClick={handleVerifyOTP}
                disabled={isLoading || otp.join("").length !== 6}
                className="w-full bg-orange-500 hover:bg-orange-600"
                data-testid="verify-otp-btn"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  <>
                    <Shield className="w-4 h-4 mr-2" />
                    Verify OTP
                  </>
                )}
              </Button>

              <div className="text-center">
                <button
                  onClick={handleResendOTP}
                  disabled={countdown > 0}
                  className={`text-sm ${countdown > 0 ? "text-slate-400" : "text-orange-500 hover:text-orange-600"}`}
                >
                  {countdown > 0 ? `Resend OTP in ${countdown}s` : "Resend OTP"}
                </button>
              </div>
            </>
          )}

          {step === "success" && (
            <div className="text-center py-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-10 h-10 text-green-500" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">
                Mobile Verified!
              </h3>
              <p className="text-slate-600">
                Your account is now verified. You'll receive a "Verified" badge on your profile.
              </p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
